/**
 * Image node for Lexical editor - simplified from lexical playground
 * https://github.com/facebook/lexical/tree/main/packages/lexical-playground
 */

import type {
    DOMConversionMap,
    DOMConversionOutput,
    DOMExportOutput,
    EditorConfig,
    LexicalNode,
    NodeKey,
    SerializedLexicalNode,
} from "lexical";
import type { JSX } from "react";

import { $applyNodeReplacement, DecoratorNode } from "lexical";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import useLexicalEditable from "@lexical/react/useLexicalEditable";
import { useLexicalNodeSelection } from "@lexical/react/useLexicalNodeSelection";
import { mergeRegister } from "@lexical/utils";
import {
    $getNodeByKey,
    $getSelection,
    $isNodeSelection,
    CLICK_COMMAND,
    COMMAND_PRIORITY_HIGH,
    COMMAND_PRIORITY_LOW,
    KEY_BACKSPACE_COMMAND,
    KEY_DELETE_COMMAND,
} from "lexical";
import ImageResizer from "./ImageResizer";

export interface ImagePayload {
    altText: string;
    height?: number | "inherit";
    key?: NodeKey;
    maxWidth?: number;
    src: string;
    width?: number | "inherit";
}

export type SerializedImageNode = SerializedLexicalNode & {
    altText: string;
    height?: number;
    maxWidth: number;
    src: string;
    width?: number;
};

function $convertImageElement(domNode: Node): DOMConversionOutput | null {
    const img = domNode as HTMLImageElement;
    const src = img.getAttribute("src");
    if (!src || src.startsWith("file:///")) {
        return null;
    }
    const altText = img.getAttribute("alt") || "";
    const width = img.getAttribute("width");
    const height = img.getAttribute("height");
    return {
        node: $createImageNode({
            altText,
            height: height ? parseInt(height, 10) : undefined,
            src,
            width: width ? parseInt(width, 10) : undefined,
        }),
    };
}

function ImageComponent({
    src,
    altText,
    nodeKey,
    width,
    height,
    maxWidth,
}: {
    altText: string;
    height: "inherit" | number;
    maxWidth: number;
    nodeKey: NodeKey;
    src: string;
    width: "inherit" | number;
}): JSX.Element {
    const imageRef = useRef<HTMLImageElement>(null);
    const [editor] = useLexicalComposerContext();
    const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey);
    const [isResizing, setIsResizing] = useState(false);
    const [resizePreview, setResizePreview] = useState<{ width: number; height: number } | null>(null);
    const [hasError, setHasError] = useState(false);
    const isEditable = useLexicalEditable();

    const isInNodeSelection = useMemo(
        () =>
            isSelected &&
            editor.getEditorState().read(() => {
                const selection = $getSelection();
                return $isNodeSelection(selection) && selection.has(nodeKey);
            }),
        [editor, isSelected, nodeKey],
    );

    const $onDelete = useCallback(
        (event: KeyboardEvent) => {
            event.preventDefault();
            const selection = $getSelection();
            if (isSelected && $isNodeSelection(selection)) {
                editor.update(() => {
                    selection.getNodes().forEach((node) => {
                        if ($isImageNode(node)) {
                            node.remove();
                        }
                    });
                });
            }
            return false;
        },
        [editor, isSelected],
    );

    useEffect(() => {
        return mergeRegister(
            editor.registerCommand(
                CLICK_COMMAND,
                (event: MouseEvent) => {
                    if (isResizing) return false;
                    // Use high priority to run before RichText which clears NodeSelection on any click
                    const nodeElement = editor.getElementByKey(nodeKey);
                    const target = event.target as Node | null;
                    const isClickOnResizeHandle = target && (target as Element).closest?.(".image-resizer");
                    if (nodeElement && target && nodeElement.contains(target) && !isClickOnResizeHandle) {
                        event.preventDefault();
                        if (!event.shiftKey) {
                            clearSelection();
                        }
                        setSelected(!isSelected);
                        return true;
                    }
                    return false;
                },
                COMMAND_PRIORITY_HIGH,
            ),
            editor.registerCommand(KEY_DELETE_COMMAND, $onDelete, COMMAND_PRIORITY_LOW),
            editor.registerCommand(KEY_BACKSPACE_COMMAND, $onDelete, COMMAND_PRIORITY_LOW),
        );
    }, [clearSelection, editor, isResizing, isSelected, nodeKey, $onDelete, setSelected]);

    const resolvedWidth = resizePreview ? resizePreview.width : width === "inherit" ? "100%" : width;
    const resolvedHeight = resizePreview ? resizePreview.height : height === "inherit" ? "auto" : height;
    const maxWidthPx = maxWidth;
    const effectiveMaxWidth = typeof resolvedWidth === "number" ? Math.max(maxWidthPx, resolvedWidth) : maxWidthPx;
    const isFocused = (isInNodeSelection || isResizing) && isEditable;

    const resizable = true;

    const onResizeMove = useCallback((width: number, height: number) => {
        setResizePreview({ width, height });
    }, []);

    const onResizeEnd = useCallback(
        (nextWidth: number, nextHeight: number) => {
            setResizePreview(null);
            editor.update(() => {
                const node = $getNodeByKey(nodeKey);
                if ($isImageNode(node)) {
                    node.setWidthAndHeight(nextWidth, nextHeight);
                }
            });
            setTimeout(() => setIsResizing(false), 200);
        },
        [editor, nodeKey],
    );

    if (hasError) {
        return (
            <div
                style={{
                    alignItems: "center",
                    border: "1px solid #ccc",
                    display: "flex",
                    minHeight: 100,
                    justifyContent: "center",
                    maxWidth: maxWidthPx,
                }}
            >
                <span style={{ color: "#999", fontSize: 12 }}>Image failed to load</span>
            </div>
        );
    }

    return (
        <span style={{ position: "relative", display: "block" }}>
            <img
                ref={imageRef}
                src={src}
                alt={altText}
                className={isFocused ? "focused" : ""}
                style={{
                    maxWidth: effectiveMaxWidth,
                    width: resolvedWidth,
                    height: resolvedHeight,
                    display: "block",
                }}
                onError={() => setHasError(true)}
                draggable={isInNodeSelection && !isResizing}
            />
            {resizable && isInNodeSelection && isFocused && (
                <ImageResizer
                    imageRef={imageRef}
                    maxWidth={maxWidthPx}
                    onResizeMove={onResizeMove}
                    onResizeStart={() => setIsResizing(true)}
                    onResizeEnd={onResizeEnd}
                />
            )}
        </span>
    );
}

export class ImageNode extends DecoratorNode<JSX.Element> {
    __src: string;
    __altText: string;
    __width: "inherit" | number;
    __height: "inherit" | number;
    __maxWidth: number;

    static getType(): string {
        return "image";
    }

    static clone(node: ImageNode): ImageNode {
        return new ImageNode(node.__src, node.__altText, node.__maxWidth, node.__width, node.__height, node.__key);
    }

    static importJSON(serializedNode: SerializedImageNode): ImageNode {
        const { altText, height, width, maxWidth, src } = serializedNode;
        return $createImageNode({
            altText,
            height: height || undefined,
            maxWidth: maxWidth || 500,
            src,
            width: width || undefined,
        });
    }

    static importDOM(): DOMConversionMap | null {
        return {
            img: () => ({
                conversion: $convertImageElement,
                priority: 0,
            }),
        };
    }

    exportDOM(): DOMExportOutput {
        const img = document.createElement("img");
        img.setAttribute("src", this.__src);
        img.setAttribute("alt", this.__altText);
        if (this.__width !== "inherit") {
            img.setAttribute("width", String(this.__width));
        }
        if (this.__height !== "inherit") {
            img.setAttribute("height", String(this.__height));
        }
        return { element: img };
    }

    constructor(
        src: string,
        altText: string,
        maxWidth: number = 500,
        width?: "inherit" | number,
        height?: "inherit" | number,
        key?: NodeKey,
    ) {
        super(key);
        this.__src = src;
        this.__altText = altText;
        this.__maxWidth = maxWidth;
        this.__width = width ?? "inherit";
        this.__height = height ?? "inherit";
    }

    exportJSON(): SerializedImageNode {
        return {
            altText: this.__altText,
            height: this.__height === "inherit" ? undefined : this.__height,
            maxWidth: this.__maxWidth,
            src: this.__src,
            type: "image",
            version: 1,
            width: this.__width === "inherit" ? undefined : this.__width,
        };
    }

    createDOM(config: EditorConfig): HTMLElement {
        const span = document.createElement("span");
        const theme = config.theme;
        const className = theme?.image;
        if (className) {
            span.className = className;
        }
        return span;
    }

    updateDOM(): false {
        return false;
    }

    setWidthAndHeight(width: "inherit" | number, height: "inherit" | number): void {
        const writable = this.getWritable();
        writable.__width = width;
        writable.__height = height;
    }

    getSrc(): string {
        return this.__src;
    }

    getAltText(): string {
        return this.__altText;
    }

    decorate(): JSX.Element {
        return (
            <ImageComponent
                src={this.__src}
                altText={this.__altText}
                nodeKey={this.__key}
                width={this.__width}
                height={this.__height}
                maxWidth={this.__maxWidth}
            />
        );
    }
}

export function $createImageNode(payload: ImagePayload): ImageNode {
    return $applyNodeReplacement(
        new ImageNode(
            payload.src,
            payload.altText,
            payload.maxWidth ?? 500,
            payload.width,
            payload.height,
            payload.key,
        ),
    );
}

export function $isImageNode(node: LexicalNode | null | undefined): node is ImageNode {
    return node instanceof ImageNode;
}
